"use client";

// Counts the time a signed-in member spends on Sweet, for /rankings, and reports it every 30 seconds
// and when the page is left (/api/me/activity). Site time runs while the page is in use (visible and
// touched in the last 5 minutes) or a video is playing; watch time is what the Player reports. The
// server never credits more than the real time since the previous report.

const REPORT_EVERY = 30_000;
const IDLE_AFTER = 5 * 60_000;

let site = 0;
const watch = new Map<string, number>();
const playing = new Set<string>();
let running = false;

/** Achievements just unlocked, for components/coins/AchievementToast to announce. */
export type Unlocked = { key: string; coins: number };
export const ACHIEVEMENTS_EVENT = "sweet:achievements";
export function announceAchievements(unlocked: Unlocked[] | undefined) {
  if (unlocked?.length) window.dispatchEvent(new CustomEvent<Unlocked[]>(ACHIEVEMENTS_EVENT, { detail: unlocked }));
}

/** Seconds of playback of a video (the Player calls this as it plays). */
export function addWatchTime(id: string, seconds: number) {
  if (running && seconds > 0) watch.set(id, (watch.get(id) ?? 0) + seconds);
}

/** A playing video keeps site time running even when the page is in the background. */
export function setPlaying(id: string, on: boolean) {
  if (on) playing.add(id);
  else playing.delete(id);
}

function report(leaving: boolean) {
  const siteWhole = Math.floor(site);
  site -= siteWhole;
  const watched: Record<string, number> = {};
  for (const [id, seconds] of watch) {
    const whole = Math.floor(seconds);
    if (whole > 0) watched[id] = whole;
    if (seconds - whole > 0) watch.set(id, seconds - whole);
    else watch.delete(id);
  }
  if (!siteWhole && !Object.keys(watched).length) return;
  const body = JSON.stringify({ site: siteWhole, watch: watched });
  // A beacon still goes out while the page is being closed; a fetch might not.
  if (leaving && navigator.sendBeacon?.("/api/me/activity", new Blob([body], { type: "application/json" }))) return;
  void fetch("/api/me/activity", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true })
    .then(async (res) => {
      if (res.status === 401) return stop();
      // Achievements just unlocked: announced by components/coins/AchievementToast.
      if (res.status === 200) {
        announceAchievements(((await res.json()) as { achievements?: Unlocked[] }).achievements);
      }
    })
    .catch(() => {});
}

let stop = () => {};

/** Starts counting for a signed-in member; returns the function that stops it. */
export function startActivity(): () => void {
  if (running) return stop;
  running = true;
  let lastInput = Date.now();
  let lastTick = Date.now();
  let lastReport = Date.now();

  const touched = () => {
    lastInput = Date.now();
  };
  const tick = () => {
    const now = Date.now();
    // Background tabs run timers about once a minute; only a playing video counts that long a gap.
    const elapsed = Math.min((now - lastTick) / 1000, playing.size ? 60 : 5);
    lastTick = now;
    const inUse = document.visibilityState === "visible" && now - lastInput < IDLE_AFTER;
    if (inUse || playing.size) site += elapsed;
    if (now - lastReport >= REPORT_EVERY) {
      lastReport = now;
      report(false);
    }
  };
  const hidden = () => document.visibilityState === "hidden" && report(true);
  const leave = () => report(true);

  const timer = window.setInterval(tick, 1000);
  const inputs = ["pointermove", "pointerdown", "keydown", "scroll", "touchstart", "wheel"] as const;
  for (const type of inputs) window.addEventListener(type, touched, { passive: true });
  document.addEventListener("visibilitychange", hidden);
  window.addEventListener("pagehide", leave);

  stop = () => {
    running = false;
    window.clearInterval(timer);
    for (const type of inputs) window.removeEventListener(type, touched);
    document.removeEventListener("visibilitychange", hidden);
    window.removeEventListener("pagehide", leave);
    site = 0;
    watch.clear();
    stop = () => {};
  };
  return stop;
}
